import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import parse from 'html-react-parser';
import moment from 'moment';
import {
  animationScrollToBottom,
  animationScrollToTop,
  convertHtmlToString,
  convertMessageToHtmlAndTrimSpaces,
  getSenderName,
  getQuillTextLength,
  isJustNowMessageCheck,
  shortenFilename
} from '../../utils';
import { ReactComponent as DownloadIcon } from 'assets/LoadCircle.svg';
import i18nContext from 'components/i18n-context';
import { MAX_MESSAGE_LENGTH, MAX_UPLOADED_FILES_SIZE } from 'components/common/constants';
import Loader from 'components/common/Loader';
import { getErrorMessageForAlert, messageRegExp, prepareFieldErrors } from 'services/utils';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import CustomFileDragAndDrop from 'components/CustomFileDragAndDrop';
import './Chat.scss';

const Chat = ({
  topic,
  messages,
  userAccount,
  isLoading,
  isFileUploading,
  error,
  clearError,
  generateSecurityCode,
  setNewMessageData,
  uploadedFiles,
  uploadDocuments,
  removeAttachment,
  getCurrentTopicMessagesFilesUrls,
  downloadAttachment,
  topicsRef,
  canWriteMessages,
  clearUploadedFiles,
  isNewMessageCreated,
  setIsNewMessageCreated,
  isShowingInternalActionConfirmation
}) => {
  const i18n = useContext(i18nContext);
  const { id } = useParams();
  const serverFieldsErrors = prepareFieldErrors(i18n, error);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const chatRef = useRef(null);
  const [messageValue, setMessageValue] = useState('');
  const previousIdRef = useRef(null);
  const hasScrolledRef = useRef(false);

  // Function to format date and time
  const formatMessageDateTime = (dateStr) => {
    const date = moment(dateStr);
    const formattedDate = date.format('DD.MM.YY');
    const formattedTime = date.format('HH:mm');
    return { date: formattedDate, time: `${formattedTime} GB` };
  };

  // Function to get day label for date separators
  const getDayLabel = (dateStr) => {
    const messageDate = moment(dateStr).startOf('day');
    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');
    const currentYear = moment().year();
    const messageYear = messageDate.year();

    if (messageDate.isSame(today)) {
      return i18n.getMessage('messages.date.today');
    } else if (messageDate.isSame(yesterday)) {
      return i18n.getMessage('messages.date.yesterday');
    } else {
      // Format as "Month Day" or "Month Day, Year"
      const monthDay = messageDate.format('MMMM D');
      if (messageYear === currentYear) {
        return monthDay; // E.g.: "December 10"
      } else {
        return `${monthDay}, ${messageYear}`; // E.g.: "December 10, 2024"
      }
    }
  };

  // Function to check if date separator should be shown
  const shouldShowDateSeparator = (currentMessage, previousMessage) => {
    if (!previousMessage) return true; // First message always shows date

    const currentDate = moment(currentMessage.created_at || currentMessage.create_at).startOf('day');
    const previousDate = moment(previousMessage.created_at || previousMessage.create_at).startOf('day');

    return !currentDate.isSame(previousDate);
  };

  useEffect(() => {
    getCurrentTopicMessagesFilesUrls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Reset state when chat changes
  useEffect(() => {
    if (id && id !== previousIdRef.current) {
      previousIdRef.current = id;
      hasScrolledRef.current = false;
    }
  }, [id]);

  // Scroll when messages are loaded
  useEffect(() => {
    // Scroll when messages are loaded and we haven't scrolled yet
    if (messages.length > 0 && !isLoading && !hasScrolledRef.current && id) {
      hasScrolledRef.current = true;

      const timer = setTimeout(() => {
        if (chatRef.current) {
          chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [messages.length, isLoading, id]);

  const form = useFormik({
    validateOnChange: false,
    initialValues: {
      message: ''
    },
    validationSchema: Yup.object({
      message: Yup.string()
        .required(i18n.getMessage('messages.error.message.empty'))
        .max(MAX_MESSAGE_LENGTH, i18n.getMessage('messages.error.message.maxSize', { size: MAX_MESSAGE_LENGTH }))
        .matches(messageRegExp, i18n.getMessage('messages.error.message.invalidFormat'))
    }),
    onSubmit: () => {
      setNewMessageData(convertMessageToHtmlAndTrimSpaces(messageValue));
      generateSecurityCode(topic.id);
    }
  });

  const { values, errors, handleSubmit, validateField, submitCount, setFieldValue, resetForm } = form;

  useEffect(() => {
    if (id) {
      resetForm();
      setMessageValue('');
      clearUploadedFiles();
    }
  }, [id]);

  useEffect(() => {
    setFieldValue('message', convertHtmlToString(messageValue));
  }, [messageValue, setFieldValue]);

  const handleMouseEnter = (index) => {
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  const onBlurMessageField = () => {
    validateField('message');
    if (serverFieldsErrors?.message) {
      clearError();
    }
  };

  useEffect(() => {
    if (isNewMessageCreated) {
      // Scroll to bottom instantly when a new message is created
      if (chatRef.current) {
        chatRef.current.scrollTop = chatRef.current.scrollHeight;
      }
      animationScrollToTop(topicsRef);
      setHoveredIndex(null);
      setFieldValue('message', '');
      setMessageValue('');
      resetForm();
      setIsNewMessageCreated(false);
    }
  }, [isNewMessageCreated]);

  return (
    <div className='chat-wrapper'>
      {isLoading ? (
        <div className='loader-container'>
          <Loader />
        </div>
      ) : (
        <>
          <div className='chat-messages-container' ref={chatRef}>
            {messages.map((message, index) => {
              const isJustNowMessageCreated = isJustNowMessageCheck(message.created_at || message.create_at);
              const isOperator = message?.sender_type === 'OPERATOR';
              const showDateSeparator = shouldShowDateSeparator(message, messages[index - 1]);
              const dateLabel = getDayLabel(message.created_at || message.create_at);

              return (
                <React.Fragment key={index}>
                  {showDateSeparator && (
                    <div className='date-separator'>
                      <span className='date-separator-text'>{dateLabel}</span>
                    </div>
                  )}
                  <div className={`chat-message ${isOperator ? 'message-operator' : 'message-user'}`}>
                    <div className='message-bubble'>
                      <div className='message-header'>
                        <span className='message-author'>{getSenderName(message?.sender_type, userAccount)}</span>
                        {isJustNowMessageCreated ? (
                          <div className='message-date'>
                            <span className='message-date-just-now'>{i18n.getMessage('messages.date.justNow')}</span>
                          </div>
                        ) : (
                          <div className='message-date'>
                            <span className='message-date-text'>
                              {formatMessageDateTime(message.created_at || message.create_at).date}
                            </span>
                            <span className='message-date-text'>
                              {formatMessageDateTime(message.created_at || message.create_at).time}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className='message-body'>{parse(message.body)}</div>
                      {message.documents.length > 0 && (
                        <div className='message-attachments'>
                          <span className='attachments-label'>
                            {i18n.getMessage('messages.attachments.label')} ({message.documents.length})
                          </span>
                          <div className='attachments-list'>
                            {message.documents.map((file, fileIndex) => {
                              const hoveredKey = `${index}-${fileIndex}`;
                              return (
                                <div
                                  key={fileIndex}
                                  className='attachment-item'
                                  onMouseEnter={() => handleMouseEnter(hoveredKey)}
                                  onMouseLeave={handleMouseLeave}
                                >
                                  {!file.url ? (
                                    <div className='attachment-preview'>
                                      <p className='placeholder-wave h-100'>
                                        <span className='placeholder w-100 h-100 skeleton' />
                                      </p>
                                    </div>
                                  ) : (file.contentType || file.content_type) === 'application/pdf' ? (
                                    <embed className='attachment-preview' src={file.url} type='application/pdf' />
                                  ) : (
                                    <img className='attachment-preview' src={file.url} alt={file.name} />
                                  )}
                                  <div className='attachment-overlay'>
                                    {hoveredIndex === hoveredKey && (
                                      <>
                                        <div className='overlay-top'>
                                          <span className='attachment-name'>{shortenFilename(file.name)}</span>
                                        </div>
                                        <div className='overlay-bottom'>
                                          <button
                                            className='attachment-download-btn'
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              downloadAttachment(file.id);
                                            }}
                                            title={i18n.getMessage('messages.attachments.download')}
                                          >
                                            <DownloadIcon />
                                          </button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {canWriteMessages && (
            <div className='chat-input-container'>
              <form action='#' id='chat-form' onSubmit={handleSubmit} className='chat-form'>
                <Input
                  className='chat-form-input'
                  type={'text-editor-quill'}
                  autoFocus={true}
                  placeholder={i18n.getMessage('messages.form.input.message.placeholder')}
                  name={'message'}
                  isDisabled={isShowingInternalActionConfirmation}
                  value={messageValue}
                  onChange={setMessageValue}
                  onBlur={onBlurMessageField}
                  initialStatus={submitCount}
                  max={MAX_MESSAGE_LENGTH}
                  rows={4}
                  error={errors?.message || serverFieldsErrors?.message}
                  /* eslint-disable-next-line max-len */
                  symbolsCountText={i18n.getMessage('messages.form.input.message.symbolsLeft', {
                    amount: MAX_MESSAGE_LENGTH - getQuillTextLength(values.message)
                  })}
                />

                <CustomFileDragAndDrop
                  files={uploadedFiles}
                  onChange={uploadDocuments}
                  onRemoveFiles={removeAttachment}
                  maxSize={MAX_UPLOADED_FILES_SIZE}
                  isDisabled={isShowingInternalActionConfirmation}
                  error={error?.type === 'attachDoc' ? getErrorMessageForAlert(i18n, error) : null}
                />

                <Button
                  className='chat-form-button'
                  type={'primary'}
                  roleType={'submit'}
                  size={'large'}
                  fullWidth={true}
                  onClick={() => {}}
                  isDisabled={isFileUploading}
                >
                  {isFileUploading ? <Loader /> : i18n.getMessage('messages.button.sendMessage')}
                </Button>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
};

Chat.propTypes = {
  isShowingInternalActionConfirmation: PropTypes.bool.isRequired,
  topic: PropTypes.object,
  messages: PropTypes.array.isRequired,
  userAccount: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isFileUploading: PropTypes.bool.isRequired,
  error: PropTypes.any,
  clearError: PropTypes.func.isRequired,
  generateSecurityCode: PropTypes.func.isRequired,
  setNewMessageData: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array,
  uploadDocuments: PropTypes.func.isRequired,
  removeAttachment: PropTypes.func.isRequired,
  getCurrentTopicMessagesFilesUrls: PropTypes.func.isRequired,
  downloadAttachment: PropTypes.func.isRequired,
  topicsRef: PropTypes.any.isRequired,
  canWriteMessages: PropTypes.bool.isRequired,
  clearUploadedFiles: PropTypes.func.isRequired,
  isNewMessageCreated: PropTypes.bool.isRequired,
  setIsNewMessageCreated: PropTypes.func.isRequired
};

export default inject((stores) => ({
  messagesStore: stores.messagesStore
}))(observer(Chat));
